import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import SheetHeader from './SheetHeader'
import { setAppStatusBarDark } from '../utils/flutterStatusBar'
import SearchBar from './SearchBar'
import LoadingSpinner from './LoadingSpinner'
import Avatar from './Avatar'
import Gap from './Gap'
import { userApi } from '../api/user'
import { Follower, Following } from '../api/user'
import { useBackgroundScrollLock } from '../hooks/useBackgroundScrollLock'

interface FriendTagBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  selectedUserIds: string[]
  /** 이름도 함께 넘긴다 — 기록 화면에서 '누구를 태그했는지' 요약에 쓴다 */
  onUserToggle: (userId: string, name: string) => void
}

const FriendTagBottomSheet: React.FC<FriendTagBottomSheetProps> = ({
  isOpen,
  onClose,
  selectedUserIds,
  onUserToggle,
}) => {
  const [users, setUsers] = useState<(Follower | Following)[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  // 드래그 관련 refs
  const dragHandleRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const startYRef = useRef(0)
  const startHeightRef = useRef(0)
  const prevIsOpenRef = useRef(false)
  const calculatedHeightRef = useRef(false)
  const dragHeightRef = useRef(0) // 드래그 중 최신 높이 (종료 시 닫기 판정용)

  // 바텀 시트 높이 설정
  const minHeight = Math.floor(window.innerHeight * 0.5) // 최소 높이: 50%
  const maxHeight = Math.floor(window.innerHeight * 0.8) // 최대 높이: 80%
  const [currentHeight, setCurrentHeight] = useState(minHeight)
  const [isDragging, setIsDragging] = useState(false)
  const [calculatedMinHeight, setCalculatedMinHeight] = useState(minHeight)
  // 최소 높이 밑으로 끌 때: 높이를 줄이지 않고 시트를 아래로 슬라이드(translateY)시켜 닫기.
  // (높이를 min 밑으로 내리면 calculateHeight가 다시 위로 튕겨 깜빡이므로 분리)
  const [translateY, setTranslateY] = useState(0)
  const dragTranslateRef = useRef(0)

  const loadUsers = useCallback(async () => {
    setIsLoading(true)
    try {
      // 팔로워와 팔로잉 모두 가져오기 (한쪽이 실패해도 나머지는 표시되도록 allSettled)
      const [followersResult, followingResult] = await Promise.allSettled([
        userApi.getFollowers(),
        userApi.getFollowing(),
      ])

      const followers =
        followersResult.status === 'fulfilled'
          ? followersResult.value.followers
          : []
      const following =
        followingResult.status === 'fulfilled'
          ? followingResult.value.following
          : []

      if (
        followersResult.status === 'rejected' ||
        followingResult.status === 'rejected'
      ) {
        console.error('팔로워/팔로잉 일부 조회 실패:', {
          followers: followersResult.status,
          following: followingResult.status,
        })
      }

      // 맞팔(서로 팔로우)만: 팔로워 ∩ 팔로잉 (내가 팔로우하고, 그 사람도 나를 팔로우)
      const followerIds = new Set(followers.map((u) => u.id))
      const mutual = following.filter((u) => followerIds.has(u.id))

      setUsers(mutual)
    } catch (error) {
      console.error('사용자 목록 조회 실패:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    setAppStatusBarDark(isOpen)
    return () => setAppStatusBarDark(false)
  }, [isOpen])

  // 시트 열린 동안 배경 스크롤 잠금 (시트 내부 스크롤 영역은 허용)
  useBackgroundScrollLock(isOpen)

  // 열릴 때마다 리프레시
  useEffect(() => {
    if (isOpen) {
      // 열릴 때마다 데이터 로드
      loadUsers()
      // 초기 높이 설정 (최대 높이를 넘지 않도록 제한)
      const initialHeight = Math.min(
        Math.max(minHeight + 200, window.innerHeight * 0.6),
        maxHeight
      )
      setCurrentHeight(initialHeight)
    }

    // 닫힐 때 검색값 초기화
    if (!isOpen) {
      setSearchValue('')
    }
  }, [isOpen, loadUsers, minHeight, maxHeight])

  // 열고 닫을 때마다 검색값·슬라이드 초기화 (isOpen 변화에만 반응)
  useEffect(() => {
    setSearchValue('')
    setTranslateY(0)
    dragTranslateRef.current = 0
  }, [isOpen])

  // 헤더 높이 측정 함수
  const calculateHeight = () => {
    if (headerRef.current && dragHandleRef.current) {
      const headerHeight = headerRef.current.offsetHeight
      const dragHandleHeight = dragHandleRef.current.offsetHeight
      const newMinHeight = Math.max(
        minHeight,
        dragHandleHeight + headerHeight + 16
      )

      setCalculatedMinHeight(newMinHeight)

      // 현재 높이가 최소 높이보다 작으면 최소 높이 + 여유 공간으로 설정 (최대 높이 제한)
      setCurrentHeight((prevHeight) => {
        if (prevHeight < newMinHeight) {
          // 최소 높이보다 충분히 크게 설정하되 최대 높이를 넘지 않도록 함
          const targetHeight = Math.max(newMinHeight + 200, minHeight + 200)
          return Math.min(targetHeight, maxHeight)
        }
        // 현재 높이가 최대 높이를 넘지 않도록 제한
        return Math.min(prevHeight, maxHeight)
      })
    }
  }

  // 모달이 처음 열릴 때 헤더 높이 측정
  const shouldCalculateHeightOnOpen =
    isOpen &&
    !prevIsOpenRef.current &&
    headerRef.current &&
    dragHandleRef.current

  if (shouldCalculateHeightOnOpen) {
    prevIsOpenRef.current = true
    calculatedHeightRef.current = false

    // DOM 업데이트 후 높이 측정을 위해 약간의 지연
    setTimeout(() => {
      calculateHeight()
      calculatedHeightRef.current = true
    }, 0)
  }

  // 로딩 완료 후에도 높이 재계산
  const shouldCalculateHeightAfterLoad =
    isOpen &&
    !isLoading &&
    calculatedHeightRef.current &&
    headerRef.current &&
    dragHandleRef.current

  if (shouldCalculateHeightAfterLoad) {
    calculatedHeightRef.current = false

    // DOM 업데이트 후 높이 측정을 위해 약간의 지연
    setTimeout(() => {
      calculateHeight()
      calculatedHeightRef.current = true
    }, 0)
  }

  // 모달이 닫힐 때 초기화
  if (!isOpen && prevIsOpenRef.current) {
    prevIsOpenRef.current = false
    calculatedHeightRef.current = false
  }

  // 드래그 시작
  const handleDragStart = useCallback(
    (clientY: number) => {
      isDraggingRef.current = true
      setIsDragging(true)
      startYRef.current = clientY
      startHeightRef.current = currentHeight
      dragHeightRef.current = currentHeight
      dragTranslateRef.current = 0
      setTranslateY(0)
    },
    [currentHeight]
  )

  // 드래그 중:
  //  - 위로 끌면 → 높이 확장(resize), translateY=0
  //  - 아래로 끌면 → 높이는 그대로 두고 시트를 그대로 아래로 슬라이드(닫기용). 축소 없음.
  const handleDragMove = useCallback(
    (clientY: number) => {
      if (!isDraggingRef.current) return

      const deltaY = startYRef.current - clientY // 위로 드래그하면 양수

      if (deltaY >= 0) {
        const h = Math.min(maxHeight, startHeightRef.current + deltaY)
        dragHeightRef.current = h
        dragTranslateRef.current = 0
        setCurrentHeight(h)
        setTranslateY(0)
      } else {
        const ty = -deltaY // 아래로 슬라이드한 양
        dragTranslateRef.current = ty
        setTranslateY(ty)
      }
    },
    [maxHeight]
  )

  // 드래그 종료: 아래로 100px 이상 슬라이드했으면 닫기, 아니면 슬라이드 원위치
  // (높이는 드래그한 그대로 유지 — 중간 축소/스냅 없음)
  const handleDragEnd = useCallback(() => {
    if (!isDraggingRef.current) return
    isDraggingRef.current = false
    setIsDragging(false)

    if (dragTranslateRef.current > 100) {
      onClose()
      return
    }

    setTranslateY(0)
  }, [onClose])

  // 마우스 이벤트 핸들러
  useEffect(() => {
    if (!isOpen) return

    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingRef.current) {
        handleDragMove(e.clientY)
      }
    }

    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        handleDragEnd()
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isOpen, handleDragMove, handleDragEnd])

  // 터치 이벤트 핸들러
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0]
    handleDragStart(touch.clientY)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDraggingRef.current) return
    e.preventDefault()
    const touch = e.touches[0]
    handleDragMove(touch.clientY)
  }

  const handleTouchEnd = () => {
    if (isDraggingRef.current) {
      handleDragEnd()
    }
  }

  // 검색 전: 내가 팔로우하는 사람들(팔로워+팔로잉) 전체를 추천으로 표시(흐린 프리뷰).
  // 검색 시: 매칭되는 친구 전체. 선택된(태그된) 친구는 항상 목록 최상단으로 모은다.
  const orderedUsers = useMemo(() => {
    const searchLower = searchValue.trim().toLowerCase()
    const selected = new Set(selectedUserIds)

    const base =
      searchLower.length === 0
        ? users
        : users.filter(
            (user) =>
              (user.nickname || '').toLowerCase().startsWith(searchLower) ||
              (user.username || '').toLowerCase().startsWith(searchLower)
          )

    return [...base].sort((a, b) => {
      const aSel = selected.has(a.id) ? 0 : 1
      const bSel = selected.has(b.id) ? 0 : 1
      if (aSel !== bSel) return aSel - bSel // 선택된 친구 먼저(상단)
      return (a.nickname || '').localeCompare(b.nickname || '', 'ko')
    })
  }, [users, searchValue, selectedUserIds])

  // 항목별 흐림: 선택(태그)된 친구만 선명, 나머지 추천은 흐리게.
  // 검색 중에는 결과 전체를 선명하게 보여준다.
  const isRowDimmed = (userId: string) =>
    !selectedUserIds.includes(userId) && searchValue.trim().length === 0

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
  }

  const handleUserClick = (userId: string) => {
    const user = users.find((u) => String(u.id) === String(userId))
    // 이름(닉네임)을 쓴다. 피드의 태그 표시도, 수정 모드에서 서버가 주는 값도
    // 이름이라 셋이 같아야 한다.
    onUserToggle(userId, user?.nickname || user?.username || '')
  }

  if (!isOpen) return null

  return (
    <>
      {/* 오버레이 */}
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-[1040]"
        onClick={onClose}
      />

      {/* 바텀 시트 컨테이너 */}
      <div className="fixed bottom-0 left-0 right-0 z-[1050] flex justify-center">
        <div className="app-container flex flex-col">
          {/* 바텀 시트 */}
          <div
            className="bg-ivoryKey rounded-t-30px shadow-lg overflow-hidden flex flex-col"
            style={{
              height: `${currentHeight}px`,
              transform: translateY ? `translateY(${translateY}px)` : undefined,
              transition: isDragging
                ? 'none'
                : 'height 0.3s ease-out, transform 0.3s ease-out',
            }}
          >
            {/* 드래그 핸들 */}
            <div
              ref={dragHandleRef}
              className="flex flex-col w-full cursor-grab active:cursor-grabbing"
              style={{ touchAction: 'none' }}
              onMouseDown={(e) => handleDragStart(e.clientY)}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="w-40px h-5px bg-d9d9d9 rounded-20px mx-auto mt-24px mb-8px"></div>
            </div>

            {/* 헤더 */}
            <div ref={headerRef} className="flex-shrink-0">
              <SheetHeader
                title="친구 태그"
                leftAction={
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex items-center justify-center cursor-pointer"
                  >
                    <img
                      src="/images/member-invite-back-arrow-icon.png"
                      alt="뒤로가기"
                      draggable={false}
                      className="w-48px h-48px object-contain"
                    />
                  </button>
                }
                rightAction={
                  <button
                    type="button"
                    onClick={onClose}
                    className="font-pretendard font-bold text-16px leading-24px text-brown-80%-main cursor-pointer whitespace-nowrap pr-20px hover:opacity-70"
                  >
                    완료
                  </button>
                }
              />

              {/* 검색바 */}
              <div className="px-20px py-16px">
                <SearchBar
                  placeholder="검색"
                  value={searchValue}
                  onSearch={handleSearchChange}
                  height={48}
                />
              </div>
            </div>

            {/* 컨텐츠 */}
            <div
              className="flex flex-col flex-1 overflow-y-auto scrollbar-hide px-20px pb-20px"
              style={{
                minHeight: 0,
                height: 'calc(80vh - 200px)', // 전체 높이에서 헤더 영역 제외
              }}
            >
              {isLoading ? (
                <div className="w-full flex items-center justify-center py-40px">
                  <LoadingSpinner size={28} borderWidth={3} />
                </div>
              ) : orderedUsers.length > 0 ? (
                <div className="w-full flex flex-col">
                  {orderedUsers.map((user) => {
                    const isSelected = selectedUserIds.includes(user.id)

                    return (
                      <React.Fragment key={user.id}>
                        <div
                          className={`
                            w-full
                            flex
                            items-center
                            gap-12px
                            py-12px
                            cursor-pointer
                            transition-opacity
                            duration-200
                            ${isRowDimmed(user.id) ? 'opacity-40' : 'opacity-100'}
                          `}
                          onClick={() => handleUserClick(user.id)}
                        >
                          {/* 프로필 이미지 */}
                          <Avatar
                            nickname={user.nickname}
                            profileImage={user.profile_image || undefined}
                            size={48}
                          />

                          {/* 사용자 정보 */}
                          <div className="flex-1 min-w-0 flex flex-col">
                            <span className="font-pretendard font-bold text-14px text-textColorText2 text-ellipsis-1">
                              {user.username}
                            </span>
                            <span className="font-pretendard font-regular text-14px text-textColorText2 text-ellipsis-1">
                              {user.nickname}
                            </span>
                          </div>

                          {/* 선택 해제: 선택된 경우에만 X 표시 */}
                          <div
                            className={`
                              flex-shrink-0
                              w-24px
                              h-24px
                              flex
                              items-center
                              justify-center
                            `}
                          >
                            {isSelected ? (
                              <button
                                type="button"
                                className="flex items-center justify-center p-0 border-0 bg-transparent cursor-pointer hover:opacity-70"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleUserClick(user.id)
                                }}
                                aria-label="선택 해제"
                              >
                                <img
                                  src="/images/friend-tag-remove-icon.png"
                                  alt=""
                                  className="w-24px h-24px object-contain"
                                />
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </React.Fragment>
                    )
                  })}
                </div>
              ) : users.length === 0 ? (
                <div className="w-full flex-1 flex flex-col items-center justify-center">
                  <p className="font-pretendard font-regular text-16px text-textColorText2 text-center">
                    팔로워 또는 팔로잉 친구가 없습니다
                  </p>
                </div>
              ) : searchValue.trim() ? (
                <div className="w-full flex-1 flex flex-col items-center justify-center">
                  <p className="font-pretendard font-regular text-16px text-textColorText2 text-center">
                    검색 결과가 없습니다
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default FriendTagBottomSheet
